/**
 * Interactive league pins for the US + Canada map SVG (viewBox 0 0 1730 1730).
 *
 * Expects each .leagues-section to contain:
 * - .leagues-map (positioning container)
 * - .leagues-map__image (the map image)
 * - .leagues-map__pins (empty pin layer)
 * - script[type="application/json"][data-leagues-json] with { leagues: [...] }
 *
 * Each league object should include:
 * memberName, location, yearJoined, link, logo, lat, lng
 */

(function () {
  'use strict';

  var MAP_SIZE = 1730;

  // Affine base fit for us_canada_colored_map.svg (Memphis + Vancouver anchors).
  var MAINLAND = {
    x: [18.009762, 2.676689, 2616.4612],
    y: [2.317595, -28.655992, 2661.192003],
  };

  // Pixel corrections measured against the live map (dx = move right, dy = move down).
  var CALIBRATION_POINTS = [
    { lat: 35.1495, lng: -90.049, dx: 0, dy: 0 },
    { lat: 33.749, lng: -84.388, dx: 26, dy: 0 },
    { lat: 30.2672, lng: -97.7431, dx: 0, dy: 0 },
    { lat: 33.5207, lng: -86.8025, dx: 16, dy: -2 },
    { lat: 42.3601, lng: -71.0589, dx: 0, dy: -66 },
    { lat: 35.2271, lng: -80.8431, dx: 3, dy: -24 },
    { lat: 41.8781, lng: -87.6298, dx: -26, dy: 0 },
    { lat: 21.3069, lng: -157.8583, dx: 0, dy: -25 },
    { lat: 49.2463, lng: -123.1162, dx: 0, dy: 0 },
    { lat: 33.7701, lng: -118.1937, dx: -67, dy: -10 },
    { lat: 45.5231, lng: -122.6765, dx: -20, dy: -12 },
    { lat: 32.7157, lng: -117.1611, dx: -81, dy: -32 },
    { lat: 26.1224, lng: -80.1373, dx: 74, dy: -52 },
    { lat: 27.9506, lng: -82.4572, dx: 46, dy: -30 },
    { lat: 28.5383, lng: -81.3792, dx: 55, dy: -27 },
    { lat: 32.2217, lng: -110.9265, dx: -80, dy: -20 },
    { lat: 33.4484, lng: -112.074, dx: -80, dy: -20 },
    { lat: 39.7392, lng: -104.99, dx: -20, dy: 30 },
    { lat:39.0997,  lng:	-94.5786, dx: -20, dy: 15 },
    { lat:43.5446,  lng:	-96.7311, dx: -30, dy: 30 },
    { lat:44.9778,  lng:	-93.265, dx: -30, dy: 30 },
    { lat:40.4406,  lng:	-79.9959, dx: 0, dy: -30 },
    { lat:39.9526,  lng:	-75.1652, dx: 5, dy: -55 },
    { lat:40.7128,  lng:	-74.0059, dx: 10, dy: -60 },
	  { lat:38.627,   lng:	-90.1994, dx: -15, dy: 20 },
  ];

  // Hawaii inset uses real lat/lng but maps to the repositioned HI islands on this SVG.
  var HAWAII = {
    x: [38.8942, 49.2308, 5236.8134],
    y: [107.6152, 153.8704, 14952.4571],
  };

  var PIN_DECADE_COLORS = {
    1970: '1970s',
    1980: '1980s',
    1990: '1990s',
    2000: '2000s',
    2010: '2010s',
    2020: '2020s',
  };

  function getPinDecadeClass(yearJoined) {
    if (yearJoined == null || yearJoined === '') {
      return '';
    }

    var match = String(yearJoined).match(/\d{4}/);
    if (!match) {
      return '';
    }

    var year = parseInt(match[0], 10);
    if (isNaN(year)) {
      return '';
    }

    var decade = Math.floor(year / 10) * 10;
    var decadeKey = PIN_DECADE_COLORS[decade];
    return decadeKey ? 'leagues-map__pin--' + decadeKey : '';
  }

  var MOBILE_BREAKPOINT = 900;

  function isDesktopLeaguesView() {
    return window.matchMedia('(min-width: ' + MOBILE_BREAKPOINT + 'px)').matches;
  }

  var REGION_NAMES = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    DC: 'District of Columbia',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
    AB: 'Alberta',
    BC: 'British Columbia',
    MB: 'Manitoba',
    NB: 'New Brunswick',
    NL: 'Newfoundland and Labrador',
    NS: 'Nova Scotia',
    NT: 'Northwest Territories',
    NU: 'Nunavut',
    ON: 'Ontario',
    PE: 'Prince Edward Island',
    QC: 'Quebec',
    SK: 'Saskatchewan',
    YT: 'Yukon',
  };

  var REGION_NAME_TO_CODE = {};

  Object.keys(REGION_NAMES).forEach(function (code) {
    REGION_NAME_TO_CODE[REGION_NAMES[code].toLowerCase()] = code;
  });

  REGION_NAME_TO_CODE.québec = 'QC';
  REGION_NAME_TO_CODE['newfoundland'] = 'NL';
  REGION_NAME_TO_CODE['labrador'] = 'NL';
  REGION_NAME_TO_CODE['pei'] = 'PE';
  REGION_NAME_TO_CODE['nwt'] = 'NT';
  REGION_NAME_TO_CODE['b.c.'] = 'BC';
  REGION_NAME_TO_CODE['b.c'] = 'BC';

  var LOCATION_SUFFIXES_TO_SKIP = {
    canada: true,
    usa: true,
    us: true,
    'united states': true,
    'united states of america': true,
  };

  function normalizeRegionCode(value) {
    var trimmed = String(value || '').trim();
    var code;

    if (!trimmed) {
      return '';
    }

    if (/^[A-Za-z]{2}$/.test(trimmed)) {
      code = trimmed.toUpperCase();
      return REGION_NAMES[code] ? code : '';
    }

    code = REGION_NAME_TO_CODE[trimmed.toLowerCase()];
    return code || '';
  }

  function parseCoordinate(value) {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    var number = Number(String(value).trim());
    return Number.isFinite(number) ? number : null;
  }

  function isHawaii(lat, lng) {
    return lat >= 18 && lat <= 23 && lng >= -162 && lng <= -154;
  }

  function calibrationOffset(lat, lng) {
    var i;
    var point;
    var distance;
    var weight;
    var weightSum = 0;
    var dx = 0;
    var dy = 0;

    for (i = 0; i < CALIBRATION_POINTS.length; i += 1) {
      point = CALIBRATION_POINTS[i];
      if (Math.abs(point.lat - lat) < 0.001 && Math.abs(point.lng - lng) < 0.001) {
        return { dx: point.dx, dy: point.dy };
      }
    }

    for (i = 0; i < CALIBRATION_POINTS.length; i += 1) {
      point = CALIBRATION_POINTS[i];
      distance = Math.sqrt(
        Math.pow(lat - point.lat, 2) + Math.pow(lng - point.lng, 2)
      );

      if (distance < 0.000001) {
        return { dx: point.dx, dy: point.dy };
      }

      weight = 1 / (distance * distance);
      dx += weight * point.dx;
      dy += weight * point.dy;
      weightSum += weight;
    }

    return { dx: dx / weightSum, dy: dy / weightSum };
  }

  function projectLatLng(lat, lng) {
    var coeffs = isHawaii(lat, lng) ? HAWAII : MAINLAND;
    var x = coeffs.x[0] * lng + coeffs.x[1] * lat + coeffs.x[2];
    var y = coeffs.y[0] * lng + coeffs.y[1] * lat + coeffs.y[2];
    var offset = calibrationOffset(lat, lng);
    x += offset.dx;
    y += offset.dy;

    return { x: x, y: y };
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function toPercent(point) {
    return {
      left: clamp((point.x / MAP_SIZE) * 100, 0, 100),
      top: clamp((point.y / MAP_SIZE) * 100, 0, 100),
    };
  }

  function getMapSize(mapEl) {
    var rect = mapEl.getBoundingClientRect();
    return Math.min(rect.width, rect.height) || MAP_SIZE;
  }

  function getPinMinDistance() {
    return window.matchMedia('(max-width: 749px)').matches ? 28 : 24;
  }

  function separateOverlappingPositions(positions, mapSizePx, minDistancePx) {
    if (positions.length < 2) {
      return positions;
    }

    var points = positions.map(function (pos, index) {
      return {
        x: (pos.left / 100) * mapSizePx,
        y: (pos.top / 100) * mapSizePx,
        index: index,
      };
    });

    var padding = minDistancePx / 2;
    var maxIterations = 100;
    var iter;
    var i;
    var j;
    var moved;
    var dx;
    var dy;
    var dist;
    var shift;
    var nx;
    var ny;
    var angle;

    for (iter = 0; iter < maxIterations; iter += 1) {
      moved = false;

      for (i = 0; i < points.length; i += 1) {
        for (j = i + 1; j < points.length; j += 1) {
          dx = points[j].x - points[i].x;
          dy = points[j].y - points[i].y;
          dist = Math.sqrt(dx * dx + dy * dy);

          if (dist >= minDistancePx) {
            continue;
          }

          if (dist < 0.001) {
            angle = ((j - i) / points.length) * Math.PI * 2;
            dx = Math.cos(angle) * minDistancePx;
            dy = Math.sin(angle) * minDistancePx;
            dist = minDistancePx;
          }

          shift = (minDistancePx - dist) / 2;
          nx = dx / dist;
          ny = dy / dist;

          points[i].x -= nx * shift;
          points[i].y -= ny * shift;
          points[j].x += nx * shift;
          points[j].y += ny * shift;
          moved = true;
        }
      }

      if (!moved) {
        break;
      }
    }

    for (i = 0; i < points.length; i += 1) {
      points[i].x = clamp(points[i].x, padding, mapSizePx - padding);
      points[i].y = clamp(points[i].y, padding, mapSizePx - padding);
      positions[points[i].index].left = (points[i].x / mapSizePx) * 100;
      positions[points[i].index].top = (points[i].y / mapSizePx) * 100;
    }

    return positions;
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function getLeagueState(league) {
    var stateCode = normalizeRegionCode(league.state);
    var location;
    var parts;
    var i;
    var part;
    var code;

    if (stateCode) {
      return stateCode;
    }

    location = league.location || '';
    parts = location
      .split(',')
      .map(function (segment) {
        return segment.trim();
      })
      .filter(Boolean);

    for (i = parts.length - 1; i >= 0; i -= 1) {
      part = parts[i];

      if (LOCATION_SUFFIXES_TO_SKIP[part.toLowerCase()]) {
        continue;
      }

      code = normalizeRegionCode(part);
      if (code) {
        return code;
      }
    }

    return '';
  }

  function toTitleCase(value) {
    var smallWords = ['of', 'and', 'the', 'in', 'for'];

    return String(value || '').replace(/\w\S*/g, function (word, offset) {
      var lower = word.toLowerCase();

      if (/^[a-z]{2}$/i.test(word)) {
        return word.toUpperCase();
      }

      if (offset > 0 && smallWords.indexOf(lower) !== -1) {
        return lower;
      }

      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
  }

  function getRegionLabel(code) {
    if (!code) {
      return 'Unknown';
    }

    return REGION_NAMES[code] || code;
  }

  function buildLeagueListItemHtml(league, state) {
    var html =
      '<li class="leagues-list__item" data-state="' + escapeHtml(state) + '">' +
      '<article class="league-card">' +
      '<div class="league-card__header">';

    if (league.logo) {
      html +=
        '<img class="league-card__logo" src="' +
        escapeHtml(league.logo) +
        '" alt="" loading="lazy">';
    } else {
      html +=
        '<div class="league-card__logo league-card__logo--placeholder" aria-hidden="true"></div>';
    }

    html += '<div class="league-card__info">';

    if (league.memberName) {
      html +=
        '<h3 class="league-card__title">' +
        escapeHtml(toTitleCase(league.memberName)) +
        '</h3>';
    }

    if (league.location) {
      html +=
        '<p class="league-card__location">' +
        escapeHtml(toTitleCase(league.location)) +
        '</p>';
    }

    html += '</div></div>';
    html += '<div class="league-card__divider" aria-hidden="true"></div>';
    html += '<p class="league-card__member">Proud Member of iPride';

    if (league.yearJoined) {
      html += ' since ' + escapeHtml(league.yearJoined);
    }

    html += '</p>';

    if (league.link) {
      html +=
        '<a class="league-card__link" href="' +
        escapeHtml(league.link) +
        '" target="_blank" rel="noopener noreferrer">Visit the League\'s Website</a>';
    }

    html += '</article></li>';
    return html;
  }

  function LeaguesMobileList(section, leagues) {
    this.section = section;
    this.leagues = leagues.slice().sort(function (a, b) {
      var nameA = (a.memberName || a.location || '').toLowerCase();
      var nameB = (b.memberName || b.location || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    this.filterSelect = section.querySelector('[data-leagues-state-filter]');
    this.listEl = section.querySelector('[data-leagues-list]');
    this.emptyEl = section.querySelector('[data-leagues-list-empty]');

    if (!this.listEl) {
      return;
    }

    this.renderFilterOptions();
    this.renderList('all');
    this.bindEvents();
  }

  LeaguesMobileList.prototype.renderFilterOptions = function () {
    var states = [];
    var seen = {};
    var self = this;

    if (!this.filterSelect) {
      return;
    }

    this.leagues.forEach(function (league) {
      var state = getLeagueState(league);
      if (!state || seen[state]) {
        return;
      }
      seen[state] = true;
      states.push(state);
    });

    states.sort(function (a, b) {
      return getRegionLabel(a).localeCompare(getRegionLabel(b));
    });

    this.filterSelect.innerHTML =
      '<option value="all">All states/provinces</option>';
    states.forEach(function (state) {
      var option = document.createElement('option');
      option.value = state;
      option.textContent = getRegionLabel(state);
      self.filterSelect.appendChild(option);
    });
  };

  LeaguesMobileList.prototype.renderList = function (selectedState) {
    var html = '';
    var visibleCount = 0;

    this.leagues.forEach(function (league) {
      var state = getLeagueState(league);
      if (selectedState !== 'all' && state !== selectedState) {
        return;
      }
      html += buildLeagueListItemHtml(league, state);
      visibleCount += 1;
    });

    this.listEl.innerHTML = html;

    if (this.emptyEl) {
      this.emptyEl.hidden = visibleCount > 0;
    }
  };

  LeaguesMobileList.prototype.bindEvents = function () {
    var self = this;

    if (!this.filterSelect) {
      return;
    }

    this.filterSelect.addEventListener('change', function () {
      self.renderList(self.filterSelect.value);
    });
  };

  function buildPopupHtml(league) {
    var html = '';

    if (league.logo) {
      html +=
        '<img class="leagues-map__popup-image" src="' +
        escapeHtml(league.logo) +
        '" alt="" loading="lazy">';
    } else {
      html +=
        '<div class="leagues-map__popup-image leagues-map__popup-image--placeholder" aria-hidden="true"></div>';
    }

    if (league.memberName) {
      html +=
        '<h3 class="leagues-map__popup-title">' +
        escapeHtml(league.memberName) +
        '</h3>';
    }

    if (league.location) {
      html +=
        '<p class="leagues-map__popup-location">' +
        escapeHtml(league.location) +
        '</p>';
    }

    html += '<div class="leagues-map__popup-divider" aria-hidden="true"></div>';

    html += '<p class="leagues-map__popup-member">Proud Member of International Pride Softball';
    if (league.yearJoined) {
      html += ' since ' + escapeHtml(league.yearJoined);
    }
    html += '</p>';

    if (league.link) {
      html +=
        '<a class="leagues-map__popup-button" href="' +
        escapeHtml(league.link) +
        '" target="_blank" rel="noopener noreferrer">Visit the League\'s Website</a>';
    }

    return html;
  }

  function LeaguesMap(section) {
    this.section = section;
    this.map = section.querySelector('.leagues-map');
    this.pinsLayer = section.querySelector('.leagues-map__pins');
    this.popup = null;
    this.lockedPin = null;
    this.hoverPin = null;
    this.leagues = [];
    this.pinElements = [];

    if (!this.map || !this.pinsLayer) {
      return;
    }

    this.readData();
    this.mobileList = new LeaguesMobileList(this.section, this.leagues);
    this.createPopup();
    this.renderPinsWhenReady();
    this.bindEvents();
  }

  LeaguesMap.prototype.readData = function () {
    var jsonEl = this.section.querySelector('[data-leagues-json]');
    if (!jsonEl) {
      return;
    }

    try {
      var payload = JSON.parse(jsonEl.textContent);
      this.leagues = Array.isArray(payload.leagues) ? payload.leagues : [];
    } catch (error) {
      console.error('Leagues map: invalid JSON payload', error);
      this.leagues = [];
    }
  };

  LeaguesMap.prototype.createPopup = function () {
    this.popup = document.createElement('div');
    this.popup.className = 'leagues-map__popup';
    this.popup.setAttribute('role', 'dialog');
    this.popup.setAttribute('aria-hidden', 'true');
    this.popup.innerHTML =
      '<button type="button" class="leagues-map__popup-close" aria-label="Close popup">&times;</button>' +
      '<div class="leagues-map__popup-body"></div>' +
      '<span class="leagues-map__popup-caret" aria-hidden="true"></span>';
    this.map.appendChild(this.popup);

    this.popupBody = this.popup.querySelector('.leagues-map__popup-body');
    this.popupClose = this.popup.querySelector('.leagues-map__popup-close');
  };

  LeaguesMap.prototype.renderPinsWhenReady = function () {
    var self = this;

    if (!isDesktopLeaguesView()) {
      return;
    }

    function render(attempt) {
      attempt = attempt || 0;

      if (getMapSize(self.map) > 0 || attempt > 30) {
        self.renderPins();
        return;
      }

      requestAnimationFrame(function () {
        render(attempt + 1);
      });
    }

    var mapImage = this.map.querySelector('.leagues-map__image');

    if (mapImage && !mapImage.complete) {
      mapImage.addEventListener(
        'load',
        function () {
          requestAnimationFrame(function () {
            self.renderPins();
          });
        },
        { once: true }
      );
      return;
    }

    render(0);
  };

  LeaguesMap.prototype.renderPins = function () {
    var self = this;

    if (!isDesktopLeaguesView()) {
      return;
    }

    this.pinsLayer.innerHTML = '';
    this.pinElements = [];

    var pendingPins = [];

    this.leagues.forEach(function (league, index) {
      var lat = parseCoordinate(league.lat);
      var lng = parseCoordinate(league.lng);

      if (lat === null || lng === null) {
        return;
      }

      var point = projectLatLng(lat, lng);
      var position = toPercent(point);

      pendingPins.push({
        league: league,
        index: index,
        left: position.left,
        top: position.top,
      });
    });

    var mapSizePx = getMapSize(this.map);
    separateOverlappingPositions(pendingPins, mapSizePx, getPinMinDistance());

    pendingPins.forEach(function (entry) {
      var pin = document.createElement('button');
      var label =
        entry.league.memberName ||
        entry.league.location ||
        'League ' + (entry.index + 1);

      pin.type = 'button';
      pin.className = [
        'leagues-map__pin',
        getPinDecadeClass(entry.league.yearJoined),
      ]
        .filter(Boolean)
        .join(' ');
      pin.style.left = entry.left + '%';
      pin.style.top = entry.top + '%';
      pin.setAttribute('aria-label', label);
      pin.dataset.index = String(entry.index);

      pin.addEventListener('mouseenter', function () {
        self.onPinHover(pin, entry.league);
      });

      pin.addEventListener('mouseleave', function () {
        self.onPinLeave(pin);
      });

      pin.addEventListener('focus', function () {
        self.onPinHover(pin, entry.league);
      });

      pin.addEventListener('blur', function () {
        self.onPinLeave(pin);
      });

      pin.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        self.onPinClick(pin, entry.league);
      });

      self.pinsLayer.appendChild(pin);
      self.pinElements.push({ pin: pin, league: entry.league });
    });
  };

  LeaguesMap.prototype.bindEvents = function () {
    var self = this;
    var resizeTimer;

    this.popupClose.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();
      self.closePopup(true);
    });

    this.map.addEventListener('click', function (event) {
      if (!self.popup.classList.contains('is-locked')) {
        return;
      }

      if (
        event.target.closest('.leagues-map__pin') ||
        event.target.closest('.leagues-map__popup')
      ) {
        return;
      }

      self.closePopup(true);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        self.closePopup(true);
      }
    });

    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        self.closePopup(true);

        if (isDesktopLeaguesView()) {
          self.renderPins();
          return;
        }

        if (self.pinsLayer) {
          self.pinsLayer.innerHTML = '';
        }
      }, 150);
    });
  };

  LeaguesMap.prototype.showPopup = function (pin, league, locked) {
    this.popupBody.innerHTML = buildPopupHtml(league);
    this.popup.style.left = pin.style.left;
    this.popup.style.top = pin.style.top;
    this.popup.classList.add('is-visible');
    this.popup.classList.toggle('is-locked', !!locked);
    this.popup.setAttribute('aria-hidden', 'false');

    this.pinElements.forEach(function (entry) {
      entry.pin.classList.toggle('is-active', entry.pin === pin);
      entry.pin.classList.toggle('is-locked', locked && entry.pin === pin);
    });
  };

  LeaguesMap.prototype.hidePopup = function () {
    this.popup.classList.remove('is-visible', 'is-locked');
    this.popup.setAttribute('aria-hidden', 'true');

    this.pinElements.forEach(function (entry) {
      entry.pin.classList.remove('is-active', 'is-locked');
    });
  };

  LeaguesMap.prototype.onPinHover = function (pin, league) {
    if (this.lockedPin) {
      return;
    }

    this.hoverPin = pin;
    this.showPopup(pin, league, false);
  };

  LeaguesMap.prototype.onPinLeave = function (pin) {
    if (this.lockedPin || this.hoverPin !== pin) {
      return;
    }

    this.hidePopup();
    this.hoverPin = null;
  };

  LeaguesMap.prototype.onPinClick = function (pin, league) {
    if (this.lockedPin === pin) {
      this.closePopup(true);
      return;
    }

    this.lockedPin = pin;
    this.hoverPin = null;
    this.showPopup(pin, league, true);
  };

  LeaguesMap.prototype.closePopup = function (clearLock) {
    if (clearLock) {
      this.lockedPin = null;
    }

    this.hoverPin = null;
    this.hidePopup();
  };

  function initLeaguesSection(section) {
    if (!section || section.dataset.leaguesMapReady === 'true') {
      return;
    }

    section.dataset.leaguesMapReady = 'true';
    new LeaguesMap(section);
  }

  function initAllLeaguesSections(root) {
    (root || document).querySelectorAll('.leagues-section').forEach(initLeaguesSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAllLeaguesSections(document);
    });
  } else {
    initAllLeaguesSections(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAllLeaguesSections(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    event.target.querySelectorAll('.leagues-section').forEach(function (section) {
      delete section.dataset.leaguesMapReady;
    });
  });
})();
